/**
 * Vendor catalog scope: manufacturers stay on their own brand; distributors
 * (Promaster today) may browse other catalog brands.
 *
 * Keep in sync with `src/utils/vendorCatalogAccess.ts`.
 */

export const MULTI_BRAND_VENDOR_TYPES = ['distributor', 'wholesaler'] as const;

/**
 * Master-vendor `type: 'distributor'` entries. Promaster is the only one today.
 * Keep in sync with `src/data/masterVendors.ts`.
 */
export const DISTRIBUTOR_BRAND_KEYS = ['promaster', 'promaster systems'] as const;

export function isMultiBrandVendorType(vendorType?: string | null): boolean {
  const t = String(vendorType || '').trim().toLowerCase();
  return (MULTI_BRAND_VENDOR_TYPES as readonly string[]).includes(t);
}

export function isDistributorBrandIdentity(brandIdOrName?: string | null): boolean {
  const key = String(brandIdOrName || '').trim().toLowerCase();
  if (!key) return false;
  return (DISTRIBUTOR_BRAND_KEYS as readonly string[]).includes(key);
}

export interface VendorCatalogInput {
  vendorType?: string | null;
  ownBrandName?: string | null;
  ownBrandId?: string | null;
  /** Extra brand ids/names this vendor may browse. `*` = full catalog. */
  catalogBrandIds?: string[] | null;
  /**
   * Flag `distributor_carry` (MOT-159): a distributor or wholesaler browses
   * only the brands on its approved carry list (`catalogBrandIds`), so an
   * empty list is its own brand, not the whole catalog. Off (the default)
   * keeps the legacy `'*'`; an explicit `'*'` entry opens the catalog either way.
   */
  requireApprovedCarry?: boolean;
}

export interface VendorCatalogScope {
  allowedBrands: string[] | '*';
  ownBrandName: string;
  /** True when the brand picker should not be locked to one brand. */
  canBrowseOtherBrands: boolean;
}

function normalizeCatalogIds(ids?: string[] | null): string[] {
  return (ids || []).map((id) => String(id || '').trim()).filter(Boolean);
}

/**
 * Resolve which catalog brands a vendor org (or impersonated brand) may see.
 *
 * - `catalogBrandIds: ['*']` is always the full catalog.
 * - A non-empty `catalogBrandIds` list is own brand plus those ids.
 * - `vendorType` distributor / wholesaler with no list: full catalog, unless
 *   `requireApprovedCarry` (flag `distributor_carry`) is set, in which case the
 *   distributor stays on its own brand until a carry request is approved.
 * - Promaster brand id is a fallback only when `vendorType` is unset
 *   (legacy orgs and brand impersonation). An explicit manufacturer stays locked.
 *   The brand name never opens the catalog (it is free text from signup).
 */
export function resolveVendorCatalogScope(input: VendorCatalogInput): VendorCatalogScope {
  const ownBrandName = String(input.ownBrandName || '').trim();
  const catalog = normalizeCatalogIds(input.catalogBrandIds);
  const wantsAll = catalog.includes('*');
  const extras = catalog.filter((id) => id !== '*');
  const vendorType = String(input.vendorType || '').trim();
  const isDistributorType = isMultiBrandVendorType(vendorType);
  const promasterFallback = !vendorType && isDistributorBrandIdentity(input.ownBrandId);
  const openByDefault = isDistributorType && !input.requireApprovedCarry;

  if (wantsAll || ((openByDefault || promasterFallback) && extras.length === 0)) {
    return { allowedBrands: '*', ownBrandName, canBrowseOtherBrands: true };
  }
  if (extras.length > 0) {
    const names = ownBrandName ? [ownBrandName, ...extras] : extras;
    const allowedBrands = Array.from(new Set(names));
    return {
      allowedBrands,
      ownBrandName,
      canBrowseOtherBrands: allowedBrands.length > 1,
    };
  }
  return {
    allowedBrands: ownBrandName ? [ownBrandName] : [],
    ownBrandName,
    canBrowseOtherBrands: false,
  };
}
